import {Audio} from '@remotion/media';
import React from 'react';
import {
	AbsoluteFill,
	Img,
	interpolate,
	staticFile,
	useCurrentFrame,
	useVideoConfig,
} from 'remotion';
import {Captions} from './Captions';
import type {AudioStoryProps} from './schema';
import {Spectrum} from './Spectrum';

const FONT = '-apple-system, BlinkMacSystemFont, "PingFang SC", "Helvetica Neue", Arial, sans-serif';
const MONO = '"SF Mono", "Menlo", "Courier New", monospace';
const TEAL = '#57e6d3';

export const AudioStory: React.FC<AudioStoryProps> = ({
	title,
	subtitle,
	episode,
	accentColor,
	audioFile,
	captionsFile: _captionsFile,
	captions,
}) => {
	const frame = useCurrentFrame();
	const {durationInFrames, fps} = useVideoConfig();

	if (!captions) {
		throw new Error('Captions are loaded in calculateMetadata()');
	}

	const progress = interpolate(frame, [0, durationInFrames - 1], [0, 1], {
		extrapolateLeft: 'clamp',
		extrapolateRight: 'clamp',
	});
	const currentSeconds = Math.floor(frame / fps);
	const totalSeconds = Math.round(durationInFrames / fps);
	const time = (seconds: number) => `00:${String(seconds).padStart(2, '0')}`;

	return (
		<AbsoluteFill style={{backgroundColor: '#06070b', fontFamily: FONT, color: 'white'}}>
			<Audio src={audioFile} />

			<div
				style={{
					position: 'absolute',
					width: 760,
					height: 760,
					left: -180,
					top: -120,
					borderRadius: '50%',
					background: accentColor,
					opacity: 0.14,
					filter: 'blur(180px)',
				}}
			/>
			<div
				style={{
					position: 'absolute',
					width: 720,
					height: 720,
					right: -220,
					bottom: -180,
					borderRadius: '50%',
					background: TEAL,
					opacity: 0.1,
					filter: 'blur(190px)',
				}}
			/>

			<div
				style={{
					position: 'absolute',
					top: 56,
					left: 64,
					right: 64,
					display: 'flex',
					alignItems: 'center',
					justifyContent: 'space-between',
				}}
			>
				<div style={{display: 'flex', alignItems: 'center', gap: 18}}>
					<div
						style={{
							width: 46,
							height: 46,
							borderRadius: '50%',
							border: `1.5px solid ${accentColor}`,
							display: 'flex',
							alignItems: 'center',
							justifyContent: 'center',
							boxShadow: `0 0 24px ${accentColor}55`,
						}}
					>
						<div
							style={{
								width: 10,
								height: 10,
								borderRadius: '50%',
								backgroundColor: accentColor,
								boxShadow: `0 0 12px ${accentColor}`,
							}}
						/>
					</div>
					<div>
						<div style={{fontSize: 32, fontWeight: 700, letterSpacing: 3}}>{title}</div>
						<div style={{fontSize: 11.5, letterSpacing: 3.6, color: 'rgba(255,255,255,0.42)', marginTop: 6}}>
							{subtitle}
						</div>
					</div>
				</div>
				<div
					style={{
						padding: '9px 18px',
						borderRadius: 999,
						border: '1px solid rgba(255,255,255,0.16)',
						fontSize: 13,
						letterSpacing: 2,
						color: 'rgba(255,255,255,0.72)',
						fontFamily: MONO,
					}}
				>
					{episode}
				</div>
			</div>

			<div
				style={{
					position: 'absolute',
					top: 128,
					left: 0,
					right: 0,
					height: 560,
					display: 'flex',
					alignItems: 'center',
					justifyContent: 'center',
				}}
			>
				<div style={{position: 'relative', width: 620, height: 620}}>
					<Spectrum src={audioFile} color={accentColor} />
					<div
						style={{
							position: 'absolute',
							inset: 0,
							display: 'flex',
							alignItems: 'center',
							justifyContent: 'center',
						}}
					>
						<Img
							src={staticFile('cover.png')}
							style={{
								width: 300,
								height: 300,
								objectFit: 'cover',
								transform: `rotate(${frame * 0.05}deg)`,
								boxShadow: `0 0 100px ${accentColor}38, 0 30px 80px rgba(0,0,0,0.7)`,
							}}
						/>
					</div>
				</div>
			</div>

			<div style={{position: 'absolute', top: 722, left: 60, right: 60}}>
				<Captions captions={captions} accentColor={accentColor} />
			</div>

			<div style={{position: 'absolute', bottom: 44, left: 64, right: 64}}>
				<div style={{height: 3, borderRadius: 2, backgroundColor: 'rgba(255,255,255,0.1)'}}>
					<div
						style={{
							height: 3,
							borderRadius: 2,
							width: `${progress * 100}%`,
							background: `linear-gradient(90deg, ${accentColor}, ${TEAL})`,
							boxShadow: `0 0 12px ${accentColor}88`,
						}}
					/>
				</div>
				<div
					style={{
						marginTop: 16,
						display: 'flex',
						alignItems: 'center',
						justifyContent: 'space-between',
						fontFamily: MONO,
						fontSize: 14,
						letterSpacing: 1.6,
						color: 'rgba(255,255,255,0.45)',
					}}
				>
					<span>
						{time(currentSeconds)} <span style={{opacity: 0.4}}>/ {time(totalSeconds)}</span>
					</span>
					<span style={{letterSpacing: 2.4}}>CODE-DRIVEN VIDEO · REMOTION 4</span>
				</div>
			</div>
		</AbsoluteFill>
	);
};
